import { Capacitor } from '@capacitor/core'

/**
 * Conf du connecteur OIDC Olympe.
 *
 * Olympe est un fournisseur OpenID Connect standard : la découverte vit sur
 * `https://api.olympe.xyz/.well-known/openid-configuration` et ne déclare que
 * le flux **Authorization Code** (réponse `code`, PKCE S256 supporté, pas de
 * `refresh_token`). L'authentification elle-même se passe sur
 * `https://account.olympe.xyz/login` (e-mail + code à usage unique), suivie
 * d'un écran de consentement pour l'application tierce.
 *
 * Ce que l'app doit obtenir d'Olympe pour que le flux aboutisse :
 *
 * - un `client_id` enregistré chez eux, avec les URIs de redirection ci-dessous
 *   déclarées **à l'identique** ;
 * - la découverte n'annonce que `client_secret_basic` / `client_secret_post`
 *   comme méthodes du point de terminaison `token` : si Olympe ne délivre pas de
 *   client public (PKCE seul), renseigner le secret dans `.env.local`
 *   (`VITE_OIDC_CLIENT_SECRET`), jamais dans le code : ce n'est pas un vrai
 *   secret dans une app installée (le binaire est lisible, PKCE reste la
 *   vraie protection du code), mais on le garde hors de l'historique git.
 *   Absent = client public (PKCE seul).
 *
 * Tant que `clientId` est vide, l'onglet Profil affiche « connexion non
 * configurée » au lieu d'ouvrir une page de login vouée à l'échec.
 */
export interface OidcConfig {
  /** émetteur : la découverte se lit à `<issuer>/.well-known/openid-configuration` */
  issuer: string
  clientId: string
  clientSecret: string | null
  /** `openid` obligatoire ; `profile` et `email` sont les deux autres scopes servis */
  scopes: string[]
  /** URI de retour dans l'app native (schéma = `appId` de `capacitor.config.ts`) */
  nativeRedirectUri: string
}

export const oidcConfig: OidcConfig = {
  issuer: 'https://api.olympe.xyz',
  clientId: 'cd6333be-b331-48f2-bf9e-868815d965de',
  clientSecret: import.meta.env.VITE_OIDC_CLIENT_SECRET || null,
  scopes: ['openid', 'profile', 'email'],
  nativeRedirectUri: 'com.olympelegends.quickview://oidc/callback',
}

/**
 * L'URI de redirection effective : le schéma de l'app en natif, l'origine de la
 * page en navigateur (dev Vite : `http://localhost:5173/`). Sans `?` ni `#` :
 * la page de login d'Olympe concatène `?code=…&state=…` telle quelle.
 */
export function redirectUri(): string {
  if (Capacitor.isNativePlatform()) return oidcConfig.nativeRedirectUri
  return `${window.location.origin}${window.location.pathname}`
}
